// src/ui/death-quotes.ts
// Snarky death banners shown when a tank is destroyed.
// Mix of classic arcade, movie quotes, and modern meme-era humor.
// {victim} = name of the player who just died.
// {killer} = name of the player who fired the killing shot (may equal victim for self-KOs).

const QUOTES: string[] = [
  // --- Classic arcade / fighting games ---
  '{victim}: FATALITY.',
  '{victim} is no more. FINISH HIM!',
  '{victim}: FLAWLESS VICTORY for {killer}.',
  'GAME OVER for {victim}.',
  '{victim} has been defeated.',
  'Player {victim} has left the battlefield.',
  '{victim} got rekt by {killer}.',
  'K.O.! {killer} sends {victim} packing.',
  'Round cleared: {victim} eliminated.',

  // --- Movie quotes ---
  '{killer}: "Hasta la vista, {victim}."',
  '{victim}: "Game over, man. Game over!"',
  '{killer}: "Say hello to my little friend." -- {victim} did not RSVP.',
  '{victim} is "not quite dead." (Okay, they are.)',
  '{killer}: "I\'ll be back." {victim} will not.',
  '{victim}: "I have a bad feeling about this."',
  '"You shall not pass!" -- but {victim} did, all the way to the ground.',
  '{killer}: "Nuke it from orbit. Only way to be sure." RIP {victim}.',
  '{killer}: "Yippee-ki-yay, {victim}."',
  '{victim} has "ceased to be." An ex-tank.',
  '{killer}: "I love the smell of napalm in the morning." Ask {victim}.',
  '{killer} made {victim} "an offer they could not refuse."',

  // --- Scorched Earth / retro war ---
  '{victim}: direct hit. Tank reduced to scrap.',
  '{victim} has been removed from the scorched earth.',
  'Dirt claims another victim: {victim}.',
  '{killer} puts {victim} six feet under.',
  '{victim} got turned into confetti by {killer}.',
  '{victim}: mission failed. Badly.',
  'Incoming! ...too late for {victim}.',

  // --- Modern / meme ---
  '{victim} has been yeeted into the void.',
  'Press F to pay respects to {victim}.',
  '{victim}: "This is fine." (It was not fine.)',
  '{victim} achievement unlocked: Get Rekt.',
  '{killer} ratio\'d {victim} with a shell.',
  '{victim} has been deleted from the server.',
  '{victim} took fall damage. A lot of fall damage.',
  '{killer} sent {victim} back to the lobby.',
  '{victim} has rage-quit (involuntarily).',
  '{victim} needs to git gud.',
  'RIP {victim}. Gone but not forgotten. (Actually, probably forgotten.)',
  '{victim} has been nuked from orbit by {killer}.',
  '{victim} skill issue.',
  '{killer} bonked {victim}.',
  '{victim}: tactical withdrawal via explosion.',
  '{victim} is in another castle.',

  // --- Self-inflicted / fall deaths feel different, but we keep killer neutral ---
  '{victim} found gravity the hard way.',
  '{victim} did not read the manual.',
  '{victim}: "wait, the ground used to be higher."',

  // --- Shorter punch lines ---
  '{victim}: eliminated.',
  '{victim}: vaporized.',
  '{victim}: disassembled.',
  '{victim}: well done.',
  '{victim}: crispy.',
];

/** Deterministic-ish quote picker. Uses Math.random if no rng supplied. */
export function pickDeathQuote(
  victim: string,
  killer: string,
  rng: () => number = Math.random,
): string {
  const template = QUOTES[Math.floor(rng() * QUOTES.length)] ?? '{victim} is down.';
  return template
    .replace(/\{victim\}/g, victim)
    .replace(/\{killer\}/g, killer);
}

export interface DeathBanner {
  text: string;
  until: number; // performance.now() timestamp when banner should disappear
}

/** Render a death banner as a bold top-center overlay. Fades out in last 500ms. */
export function drawDeathBanner(
  ctx: CanvasRenderingContext2D,
  banner: DeathBanner,
  gameW: number,
  now: number,
): void {
  const remaining = banner.until - now;
  if (remaining <= 0) return;
  const fade = remaining < 500 ? remaining / 500 : 1;

  ctx.save();
  ctx.globalAlpha = fade;

  // Measure text to size the backdrop
  ctx.font = 'bold 28px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  const metrics = ctx.measureText(banner.text);
  const padX = 24;
  const padY = 14;
  const bw = Math.min(gameW - 40, metrics.width + padX * 2);
  const bh = 28 + padY * 2;
  const bx = (gameW - bw) / 2;
  const by = 24;

  // Backdrop
  ctx.fillStyle = 'rgba(0, 0, 0, 0.72)';
  ctx.fillRect(bx, by, bw, bh);
  ctx.strokeStyle = 'rgba(255, 80, 80, 0.9)';
  ctx.lineWidth = 2;
  ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);

  // Text
  ctx.fillStyle = '#ffd6d6';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(banner.text, gameW / 2, by + bh / 2);

  ctx.restore();
}
